import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {EdgeProps, Position, useReactFlow, EdgeLabelRenderer} from 'reactflow';

import {assert} from 'Asserts';
import ProcessFlowchartContext from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartContext';
import {
  ProcessFlowchartEdgeType,
  ProcessFlowchartStaticEdge,
  PROCESS_FLOWCHART_EDGE_DEFAULT_COLOR,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {getEdgeRenderingData} from 'processes/graph-builder-processes/flowchart-viz/util/getEdgeRenderingData';
import omitKey from 'util/omitKey';

import EdgeLabel from './EdgeLabel';

export interface ProcessFlowchartStaticEdgeRendererProps extends EdgeProps {
  data?: ProcessFlowchartStaticEdge;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  markerEnd?: string;
  label?: string;
}
function ProcessFlowchartStaticEdgeRenderer({
  data,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  label,
  ...props
}: ProcessFlowchartStaticEdgeRendererProps): JSX.Element {
  assert(data != null, 'Unable to render a flowchart edge without data');
  const reactFlowInstance = useReactFlow();

  const targetNode = reactFlowInstance.getNode(data.toNodeClientId);
  const sourceNode = reactFlowInstance.getNode(data.fromNodeClientId);

  const nodes = reactFlowInstance.getNodes();
  const edges = reactFlowInstance.getEdges();

  const {flowchartClientId} = ProcessFlowchartContext.useContext();

  const {edgePath, labelX, labelY} = getEdgeRenderingData(
    omitKey(data, 'hasBeenUsedToCreateData'),
    {sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition},
    nodes,
    edges,
    false,
    flowchartClientId,
    sourceNode,
    targetNode,
    data.layout
  );

  return (
    <>
      <svg className={css(styles.edgeSvg)}>
        <path id={data.clientId} className={css(styles.edge)} d={edgePath} markerEnd={markerEnd} />
      </svg>
      {label != null && (
        <EdgeLabelRenderer>
          <EdgeLabel
            label={label}
            labelX={labelX}
            labelY={labelY}
            labelShowBg={props.labelShowBg}
            labelStyle={props.labelStyle}
            labelBgPadding={props.labelBgPadding}
            labelBgBorderRadius={props.labelBgBorderRadius}
            labelBgStyle={props.labelBgStyle}
          />
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  edgeSvg: {
    overflow: 'visible',
  },
  edge: {
    stroke: PROCESS_FLOWCHART_EDGE_DEFAULT_COLOR,
    fill: 'none',
  },
});

export const PROCESS_FLOWCHART_EDGE_TYPE_TO_STATIC_EDGE_RENDERER = {
  [ProcessFlowchartEdgeType.STATIC_VIEWER]: ProcessFlowchartStaticEdgeRenderer,
};
